/**
 * The M7 render seam. (§6)
 *
 * `renderer.ts` keeps the EXACT public API `main.ts` drives (`beginFrame`,
 * `setPlayer`, `setBall`, `setPitch`, `applyCamera`, `render`, …). Internally it
 * is now a thin shell that captures each frame into a `RenderState` and delegates
 * the drawing to a set of `RenderSubsystem`s — pitch, players, lighting, stadium,
 * VFX, post. This is the same move that let the sim be built by parallel agents:
 * one frozen interface, many independent implementations, wired in one place.
 *
 * Why this shape:
 *   - main.ts and the netcode never learn that graphics got richer — the seam
 *     they hold is unchanged, so M7 cannot regress M1–M6 gameplay.
 *   - Subsystems never edit each other or the shell. Each owns one file and
 *     implements this interface, so five agents don't collide on renderer.ts.
 *   - Graphics is strictly DOWNSTREAM of the sim (§15.2). A subsystem may READ
 *     RenderState and use `Math.*` or the wall clock freely, but nothing here
 *     may write back into WorldState — it never even sees one. The render state
 *     is an already-interpolated, presentation-only copy.
 *
 * ── The per-frame data contract ────────────────────────────────────────────
 *
 * main.ts calls `setPlayer(id,x,y,z,yaw,team)` / `setBall(...)` etc. The shell
 * writes those into `RenderState` (structure-of-arrays, allocated once). It does
 * NOT carry the sim's AnimState or per-player velocity, because the seam we chose
 * to preserve doesn't pass them. Subsystems that need dynamics DERIVE them:
 * `RenderState` also exposes the PREVIOUS frame's transforms and the frame dt, so
 * a player system computes velocity as (pos - prevPos)/dt and picks a locomotion
 * blend from that. This is exactly what a 2D locomotion blendspace consumes, and
 * it needs zero change to main.ts. Upper-body actions (pass/tackle/shoot) that
 * genuinely need the real AnimState are the documented mocap-ready seam (D9) —
 * enrich the setter deliberately later, don't fake it now.
 */

import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import type { MatchConfig } from '@fg/shared/sim';
import type { RefSignal } from './refSignal.js';
import type { AttachmentRegistry, BallHoldPose } from './handAttach.js';

/** Auto-detected + manually overridable. (§6.8) Low must hit 60fps on Intel iGPU. */
export type QualityTier = 'low' | 'medium' | 'high' | 'ultra';

/** Ordered low→ultra, for `tier >= 'high'` style comparisons via QUALITY_RANK. */
export const QUALITY_RANK: Record<QualityTier, number> = {
  low: 0,
  medium: 1,
  high: 2,
  ultra: 3,
};

/**
 * Layer for GPU-placed impostors: instanced grass, grass clumps, the crowd
 * billboards and the hero flags. Everything on it shares one property — the
 * mesh's real position exists ONLY in its custom vertex shader, computed from
 * per-instance attributes (`aOffset`/`aParam`/`iOffset`). The base geometry is
 * a unit quad sitting at the world origin.
 *
 * That makes them unsafe for any pass that substitutes its own material.
 * `scene.overrideMaterial` keeps the geometry and the instance count but throws
 * away the vertex shader that does the placing, so all ~190,000 quads collapse
 * into a stack at the origin — which, on a football pitch, is the centre circle
 * and therefore always on camera. The SSAO normal pass is exactly such a pass.
 *
 * Meshes on this layer are moved OFF the default layer (`layers.set`, not
 * `enable`), so a camera sees them only after an explicit `layers.enable`.
 * The scene camera does that once; the normal pass masks it back off per frame.
 */
export const IMPOSTOR_LAYER = 1;

export interface QualitySettings {
  tier: QualityTier;
  /** CSM shadow map resolution per cascade (0 = shadows off). (§6.2) */
  shadowMapSize: number;
  shadowCascades: number;
  /** Near-field GPU grass instance count. (§6.3) */
  grassInstances: number;
  /** Crowd billboard count. (§6.6) */
  crowdInstances: number;
  /** Whole post stack on/off, plus the expensive members individually. (§6.1) */
  postEnabled: boolean;
  ssao: boolean;
  bloom: boolean;
  motionBlur: boolean;
  taa: boolean;
  /** LOD distance multiplier — <1 switches to cheaper meshes sooner. (§6.4) */
  lodBias: number;
  /** Device pixel ratio ceiling. */
  maxPixelRatio: number;
}

/**
 * Shared services every subsystem gets at init. Read-only handles to the things
 * the shell owns; a subsystem adds its own objects to `scene` and never swaps
 * these out.
 */
export interface RenderContext {
  readonly gl: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  quality: QualitySettings;
  /** Viewport in device pixels — updated on resize before `onResize` fires. */
  viewportWidth: number;
  viewportHeight: number;
  /**
   * PROP ATTACHMENT (see handAttach.ts). The one channel through which a
   * subsystem may read another's animated bones — a player subsystem publishes
   * hand anchors here, the ball subsystem asks for one. Subsystems still never
   * import each other; they both hold this.
   */
  attachments: AttachmentRegistry;
}

/**
 * Per-frame, presentation-only entity state. Structure-of-arrays, allocated once
 * in the shell, mutated in place — the render path allocates nothing (§15.7).
 *
 * Indices are PlayerId (0..MAX_PLAYERS-1). `active[i]` says whether slot i was
 * set this frame; a subsystem must skip inactive slots (they hold stale data).
 */
export interface RenderState {
  /** Whether slot i was written via setPlayer this frame. */
  active: Uint8Array;
  team: Uint8Array;
  /** Current-frame transforms (feet position; yaw measured +X→+Z, sim convention). */
  px: Float32Array;
  py: Float32Array;
  pz: Float32Array;
  yaw: Float32Array;
  /** Previous-frame transforms, for deriving velocity/step cadence. NaN-free:
   *  seeded to the current value the first time a slot goes active. */
  prevPx: Float32Array;
  prevPy: Float32Array;
  prevPz: Float32Array;
  /**
   * GROUND VELOCITY, DERIVED OVER THE INTERVAL THAT ACTUALLY ELAPSED.
   *
   * ⚠ DO NOT GO BACK TO `(px - prevPx) / dt`. That was here, and it is the
   * defect the owner filmed as *"there is a jitter when you continue the walk"*.
   * The sim steps on a FIXED 60 Hz tick and the renderer draws at 150-180 fps,
   * so two render frames in three are handed the SAME position as the frame
   * before. On those, `px - prevPx` is exactly zero and the derived speed is
   * zero — the animator picks Idle. On the third, one whole tick of travel is
   * divided by one render frame's `dt`, which reads 2-3x too fast — the
   * animator picks Run. Filmed at 30 fps while walking at 1.548 m/s, the body
   * alternated a STANDING pose and a mid-stride pose every two frames, about
   * 7.5 Hz, forever.
   *
   * The signal is 60 Hz; sampling it at 150 Hz cannot make it finer. So the
   * velocity is recomputed only when the position actually CHANGES, over
   * `sampleAgeS` — the real time since the previous change — and held between
   * changes. That is exact rather than smoothed: no lag on a step change, and
   * no invented frequency content.
   */
  velX: Float32Array;
  velZ: Float32Array;
  /**
   * Seconds since this slot's position last changed. Reset on every distinct
   * sample; past `STILL_AFTER_S` the slot is genuinely stationary and its
   * velocity is zeroed, so a held pose can never keep walking on the spot.
   */
  sampleAgeS: Float32Array;
  /**
   * The sim's AnimState per slot (D9 seam enrichment, 2026-07-17). Carried so
   * the player subsystem can play action clips (pass/tackle/jump/bicycle)
   * instead of deriving everything from velocity. 0 (Idle) when the caller
   * doesn't supply one; subsystems must treat unknown values as locomotion.
   */
  anim: Uint8Array;
  /**
   * THE ACTION EXECUTION ID (§11), straight off `players.animExecId`.
   *
   * `anim` above is a LEVEL — the sim holds a strike state for a whole window,
   * so it is the same value on many consecutive frames. This is the EVENT
   * identity beside it: it changes exactly once per action the sim accepted, so
   * a subsystem can answer "has a NEW action started since the last frame I
   * drew?" without inferring it. That question is not answerable from `anim`
   * (two passes in a row never change it) and not answerable from the frame
   * number (a throttled or interpolated frame repeats one).
   *
   * Compare for INEQUALITY ONLY. The value is wrapped to a small field on the
   * wire (sim/anim-exec.ts) — differences are meaningful, ordering is not.
   */
  animExec: Uint8Array;
  /** Seconds since the previous rendered frame. Never zero (clamped). */
  dt: number;
  /** Monotonic seconds since renderer construction — drives shaders/wind/crowd. */
  elapsed: number;

  ball: {
    px: number; py: number; pz: number;
    qx: number; qy: number; qz: number; qw: number;
    /** Derived speed (m/s) for the trail threshold (§6.7). */
    speed: number;
    /**
     * Mirrors `BallState.powerKick` (sim/types.ts) — true from the instant
     * this flight launched EITHER under boost tier 2 or at full charge.
     * Owner, 2026-08-15: "when the ball is kicked with boost 2 the ball
     * should have those trails red around the ball like a wave following
     * the ball", then "also on full power". Local play (practice/solo) feeds
     * the sim's own value straight through; a remote player's ball over the
     * network is decoded off the snapshot wire's `BF_POWERKICK` bit
     * (net/snapshot.ts, net/interpolation.ts's `ballPowerKick`).
     */
    powerKick: boolean;
    /**
     * Mirrors `BallState.possessedBy` (sim/types.ts) — the slot currently in
     * close control of the ball, or -1. Same treatment as `powerKick` above:
     * local play feeds the sim's own value straight through, a remote game
     * decodes it off the snapshot wire (net/interpolation.ts's
     * `ballPossessedBy`, already computed there and previously unread).
     *
     * NOT the same question as `ballHeldBy` below — that is a GK's/thrower's
     * HANDS specifically. This is "whose feet", which is what the gait layer
     * needs to know to play the close-control dribble clip instead of a plain
     * run (owner, 2026-08-23: "the dribble where the ball is move left foot
     * to right").
     */
    possessedBy: number;
  };
  ballPrev: { px: number; py: number; pz: number };

  /**
   * IS THE BALL IN SOMEBODY'S HANDS, AND WHOSE. (§4.5, Law 15)
   *
   * Fed by `Renderer.setBallHold`, derived in `game/ballHold.ts` from
   * authoritative sim state, defaulted to "loose" in `beginFrame` — so a loop
   * that never feeds it draws exactly the ball it drew before this existed.
   *
   * This says WHO, never WHERE. `ball.px/py/pz` above remains the only ball
   * position on this struct and stays exactly what the sim says; the drawn
   * mesh's bounded, hold-only correction toward the animated hand lives
   * entirely inside `ball.ts`. Nothing else may read these two fields to move
   * anything — the camera, the HUD, the trail and the shadow all follow the
   * simulated ball, which is the ball everything else in the game agrees on.
   */
  ballHeldBy: number;
  ballHoldPose: BallHoldPose;

  /**
   * Controlled-player indicator (§4.8). The seam feeds a POSITION, not an id
   * (see setControlled), so subsystems get the ring's world position + blend +
   * a pulse phase, and derive nothing about which slot it is.
   */
  controlledShow: boolean;
  controlledX: number;
  controlledY: number;
  controlledZ: number;
  /** 0..1 switch-blend for the ring. */
  controlledBlend: number;
  /** Seconds, for the ring's pulse animation. */
  controlledPulse: number;
  /** Aim/facing yaw (radians) of the controlled player — drives the facing
   *  chevron inside the ring so you always know which way you point, even off the
   *  ball. Mouse-driven (aimYaw), so it updates continuously as you look. (§5) */
  controlledYaw: number;

  /**
   * THE STAMINA BOOST AURA (owner, 2026-08-13: *"add a red aura on player when
   * he activates it please"*). 0 = off, 1 = tier 1, 2 = tier 2.
   *
   * ⚠ A SLOT, unlike `controlled*` above, WHICH IS DELIBERATELY ONLY A POSITION.
   * The ring is a mark on the grass and needs nothing but a spot; the aura is an
   * inverted hull built from the player's OWN animated geometry (boostAura.ts),
   * so it has to be able to ask which meshes are being drawn for him — and that
   * question only has a slot-shaped answer. The position rides along for any
   * future ground-level element and so a consumer never has to look a slot's
   * transform up for itself.
   *
   * ⚠ THIS IS THE LOCAL PLAYER'S TIER AND ONLY HIS. `InputFlags.Boost1/2` reach
   * the sim as per-tick intent and are never stored on `WorldState`, so a remote
   * player's tier is not knowable on this client without new SIM state. Each
   * game loop feeds its own `input.state.boostTier` beside its `setControlled`
   * call; nothing here tries to guess anyone else's.
   */
  boostAuraTier: number;
  boostAuraSlot: number;
  boostAuraX: number;
  boostAuraY: number;
  boostAuraZ: number;

  /** §7.3 off-ball ball-direction arrow. */
  ballArrowShow: boolean;
  ballArrowX: number;
  ballArrowY: number;
  ballArrowZ: number;
  ballArrowYaw: number;

  /**
   * Pass-target indicator (§4.2, Phase 3C): the faint circle under the teammate
   * the charging pass would go to. Fed by POSITION each frame (like the
   * controlled ring) while a pass is charging; the game loop derives it from
   * the SAME shared choosePassTarget() the sim strikes with. Render-only.
   */
  passTargetShow: boolean;
  /** Receiver slot the ring is on; a CHANGE snaps it instead of easing. */
  passTargetId: number;
  passTargetX: number;
  passTargetY: number;
  passTargetZ: number;

  /**
   * Kick-aim arrow (LIVE FOOTBALL POLISH §2/§5): a ground arrow from the
   * controlled player pointing along its live `aimYaw` — "which way would a
   * shot/pass go right now". Fed by POSITION each frame (same pattern as
   * `controlled*`) ONLY while that player has controllable possession — the
   * game loop threads `ctx.possessing` through so this subsystem derives
   * nothing about which slot has the ball, exactly like the controlled ring
   * derives nothing about which slot is driven. `aimArrowCharge` is the active
   * shoot/pass charge fraction (0..1, 0 when nothing is charging) and scales
   * the arrow's length/opacity — a client-side visual only, never fed back
   * into the sim.
   */
  aimArrowShow: boolean;
  aimArrowX: number;
  aimArrowY: number;
  aimArrowZ: number;
  aimArrowYaw: number;
  aimArrowCharge: number;
  /** Live curl accumulator (InputState.curl, [-1, 1]) — bends the aim arrow's
   *  curve-shaft indicator so a lateral mouse drag during a charge is visible,
   *  not just felt in the eventual ball flight (§3 playtest feedback). 0 when
   *  nothing is charging (curl decays to 0 outside a charge). */
  aimArrowCurl: number;

  /**
   * CORNER LANDING TRAIL (owner, 2026-08-09) — the projected delivery arc and
   * where it touches down, drawn by render/cornerTrail.ts.
   *
   * Fed per frame by whichever game loop owns the WorldState, from
   * `ai/restart-preview.ts previewCornerDelivery`, and defaulted off in
   * `beginFrame` so a loop that never feeds it gets nothing rather than a stale
   * arc. `cornerTrailPoints` is a flat [x,y,z, ...] run BORROWED from the
   * predictor's own buffer — the renderer copies out of it in the same frame and
   * never retains it.
   *
   * ⚠ `cornerTrailExact` is the honesty bit. False means the sim would roll a
   * strike error this preview cannot know, and the subsystem must then dim the
   * arc and draw NO landing ring. See cornerTrail.ts's header for why that
   * distinction is the whole reason this marker is allowed to exist when the
   * old fixed-distance one was deleted.
   */
  cornerTrailShow: boolean;
  cornerTrailPoints: Float32Array;
  cornerTrailCount: number;
  cornerTrailLandX: number;
  cornerTrailLandZ: number;
  cornerTrailLanded: boolean;
  cornerTrailExact: boolean;

  /**
   * REFEREE SIGNAL (§5.6) — the one channel on this struct that carries an EDGE
   * rather than per-frame state, and the seam render/referee.ts's header has been
   * waiting for ("If the setter is ever enriched with a referee-signal … the
   * action overlay hook here is ready").
   *
   * Everything else here is re-fed every frame by main.ts/practice.ts and
   * defaulted off in `beginFrame`. A rule event is not like that: the sim raises
   * `WHISTLE` (rules/fsm.ts, on the restart Setup→Ready flip) or a card-bearing
   * `FOUL`/`PUNCH`/`KICK` exactly ONCE and never again, so this channel is
   * PUSHED by `Renderer.signalReferee()` and expires on its own timer — see
   * refSignal.ts's `ageRefSignal`, which the shell runs in `beginFrame`. Without
   * that self-clear a whistle would still be showing at full time.
   *
   * `kind === RefSignalKind.None` means "the referee is signalling nothing";
   * subsystems must treat that as the normal case, not an error.
   *
   * Strictly downstream (§15.2): read by the referee subsystem, never written
   * back into a WorldState. The sim decides; this only reports.
   */
  refSignal: RefSignal;

  /**
   * STADIUM SCOREBOARD FEED (2026-08-02). Match presentation for the in-world
   * LED screens: names, score, clock. Fed per-frame by whichever game loop owns
   * the WorldState (same pattern as the HUD); `show` defaults false in
   * beginFrame so a loop that never feeds it simply gets blank boards, never
   * stale ones. The stadium repaints its canvas texture only when a VALUE
   * changes -- feeding every frame costs nothing.
   */
  scoreboard: {
    show: boolean;
    homeName: string;
    awayName: string;
    scoreA: number;
    scoreB: number;
    /** Match clock, seconds. */
    clockS: number;
    /** Short phase label: "1ST", "2ND", "HT", "FT", or empty. */
    phase: string;
  };

  /** The match config the pitch/stadium/goals were built for, or null pre-kickoff. */
  config: MatchConfig | null;
}

/**
 * A pluggable render subsystem. Lifecycle:
 *   init         once, after the shell has gl/scene/camera
 *   onPitchConfig  whenever setPitch runs (new match / size, §9.2) — build/rebuild
 *                  size-dependent geometry (pitch, goals, stadium, nets)
 *   update       every frame, before the shell composites — read RenderState,
 *                move meshes, advance shaders. NO allocation here.
 *   setQuality   when the tier changes — rescale instance counts, toggle passes
 *   onResize     viewport changed
 *   dispose      free GPU resources
 *
 * Every method except `update` is optional; a subsystem implements what it needs.
 */
export interface RenderSubsystem {
  readonly name: string;
  init?(ctx: RenderContext): void;
  onPitchConfig?(config: MatchConfig, ctx: RenderContext): void;
  update(state: RenderState, ctx: RenderContext): void;
  setQuality?(quality: QualitySettings, ctx: RenderContext): void;
  onResize?(ctx: RenderContext): void;
  dispose?(): void;
}

/**
 * The final-composite subsystem (post-processing). At most one is registered.
 * If present, the shell hands it the whole render; if absent, the shell calls
 * `gl.render(scene, camera)` directly. This keeps the EffectComposer entirely
 * inside the post subsystem's file — the shell knows only "someone composites".
 */
export interface CompositeSubsystem extends RenderSubsystem {
  /** Draw the final frame. Replaces the shell's direct gl.render. */
  composite(ctx: RenderContext): void;
}

export function isCompositeSubsystem(s: RenderSubsystem): s is CompositeSubsystem {
  return typeof (s as CompositeSubsystem).composite === 'function';
}

// ---------------------------------------------------------------------------
// Quality tier presets + auto-detection (§6.8)
// ---------------------------------------------------------------------------

export const QUALITY_PRESETS: Record<QualityTier, QualitySettings> = {
  low: {
    tier: 'low',
    shadowMapSize: 0, // no real-time shadows on an Intel iGPU — must hold 60fps (§6.8)
    shadowCascades: 0,
    grassInstances: 0,
    crowdInstances: 2_000,
    postEnabled: false,
    ssao: false,
    bloom: false,
    motionBlur: false,
    taa: false,
    lodBias: 0.6,
    maxPixelRatio: 1,
  },
  medium: {
    tier: 'medium',
    shadowMapSize: 1024,
    shadowCascades: 2,
    grassInstances: 40_000,
    crowdInstances: 12_000,
    postEnabled: true,
    ssao: false,
    bloom: true,
    motionBlur: false,
    taa: false,
    lodBias: 0.85,
    maxPixelRatio: 1.5,
  },
  high: {
    tier: 'high',
    shadowMapSize: 2048,
    shadowCascades: 3,
    // Measured at 3840x2160: grass + clumps + crowd cost 0.97 ms of main-pass
    // fill, ~15% of the frame, and they are the meshes that make a weak GPU
    // fall over. Halving both keeps the pitch and bowl visually populated at
    // broadcast distance — where blade density stops being resolvable — while
    // returning most of that. Ultra keeps the full figures.
    grassInstances: 60_000,
    crowdInstances: 20_000,
    postEnabled: true,
    ssao: true,
    bloom: true,
    motionBlur: true,
    taa: true,
    lodBias: 1,
    // Every fragment-bound cost in the frame — the post stack, SSAO, grass,
    // crowd — is multiplied by pixelRatio^2. A 4K panel at 200% Windows scaling
    // reports DPR 2, so this ceiling was asking a "High" machine to shade four
    // times the pixels of a DPR-1 one. Measured at 3840x2160: dropping the
    // ratio from 1.5 to 1 took the frame from 5.52 ms to 2.84 ms — 49% of what
    // remained. 1.25 keeps text and pitch lines crisp on a HiDPI panel without
    // paying for a full second doubling.
    maxPixelRatio: 1.25,
  },
  ultra: {
    tier: 'ultra',
    shadowMapSize: 2048,
    shadowCascades: 3,
    grassInstances: 200_000, // §6.3's near-field figure
    crowdInstances: 56_000, // 4-tier bowl (stadium.ts CROWD_MAX): lower tiers at the old packing, upper tiers thinned
    postEnabled: true,
    ssao: true,
    bloom: true,
    motionBlur: true,
    taa: true,
    lodBias: 1.25,
    // Ultra is opt-in and names a machine that can afford the pixels, so it
    // keeps more headroom than High — but not the full DPR 2 doubling, for the
    // same fragment-cost reason. See High above for the measurement.
    maxPixelRatio: 1.5,
  },
};

/**
 * Cheap heuristic auto-detect. Real tuning is a benchmark pass (§9), but this
 * keeps a laptop from booting into Ultra and a workstation into Low. Reads the
 * GL renderer string + core count; deliberately conservative — it is better to
 * start a tier low and let the user raise it than to stutter on first frame.
 */
export function autoDetectQuality(gl: WebGLRenderer): QualityTier {
  const ctx = gl.getContext();
  let rendererStr = '';
  try {
    const dbg = ctx.getExtension('WEBGL_debug_renderer_info');
    if (dbg) rendererStr = String(ctx.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '');
  } catch {
    rendererStr = '';
  }
  const r = rendererStr.toLowerCase();
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4;

  // Software rasterisers and virtual adapters FIRST — before anything else can
  // claim them. These report no recognisable GPU family, so every later branch
  // used to miss them and they fell through to the core-count guess. A software
  // device handed the High tier renders the SSAO normal pass, 120k grass and a
  // 2048 shadow map on the CPU: measured on SwiftShader, that is 0 frames in
  // 6 seconds. Even Low is generous here, but it is at least playable.
  if (/(swiftshader|llvmpipe|basic render|software|microsoft basic|virtualbox|vmware|parallels)/.test(r)) {
    return 'low';
  }

  // Integrated graphics → Low, unconditionally (§6.8's hard requirement).
  if (/(intel|uhd|iris|hd graphics|apple m|adreno|mali|powervr)/.test(r)) {
    // Apple Silicon is strong enough for Medium despite being "integrated".
    if (/apple m/.test(r)) return 'high';
    return 'low';
  }

  // AMD's integrated parts do not say "integrated" anywhere in their renderer
  // string — they report as plain "AMD Radeon(TM) Graphics", or by APU family
  // (Vega 8, 660M/680M/780M), or as a low-end mobile discrete part (Radeon 610M).
  // None of those match the Intel-flavoured regex above, so they used to reach
  // the `cores >= 8` fallback and boot straight into High on hardware that
  // cannot sustain it. Same for NVIDIA's MX/T/Quadro mobile line.
  if (/(radeon\(tm\) graphics|radeon graphics|vega \d|radeon \d{3}m|geforce mx|nvidia t\d{3}|quadro)/.test(r)) {
    return 'low';
  }

  if (/(rtx 40|rtx 30|rtx 20|radeon rx [67]|geforce rtx)/.test(r)) return 'ultra';
  if (/(gtx 16|gtx 10|radeon rx [45]|geforce gtx)/.test(r)) return 'high';

  // UNKNOWN GPU. Core count says nothing about fill rate — virtually every
  // current CPU reports 8+ logical cores, so `cores >= 8 -> high` was in
  // practice "everything unrecognised is High". That is the wrong direction for
  // a heuristic this function's own doc comment calls "deliberately
  // conservative": High turns on SSAO, 120k grass, 42k crowd and a 2048 shadow
  // map. Guess Medium instead and let `createAdaptiveQuality` below promote or
  // demote from a measurement, which is worth more than any string match.
  if (cores >= 8) return 'medium';
  if (cores >= 4) return 'medium';
  return 'low';
}

/** One step down the tier ladder. `low` is the floor. */
export function lowerTier(tier: QualityTier): QualityTier {
  if (tier === 'ultra') return 'high';
  if (tier === 'high') return 'medium';
  if (tier === 'medium') return 'low';
  return 'low';
}

/**
 * Measured auto-downgrade. String matching cannot know a user's resolution,
 * their display scaling, what else is on their GPU, or whether the browser fell
 * back to a software path — so the detector above is a starting guess and this
 * is the correction. It watches real frame times and steps the tier down when
 * the guess is plainly wrong.
 *
 * Deliberately one-way. A tier that oscillates is worse than a tier that is
 * merely too low: every downgrade rebuilds the composer and the shadow map, and
 * a user who sees the game get prettier and then stutter again will (rightly)
 * call that a bug. The user can always raise the tier by hand in Settings.
 *
 * The first `warmupFrames` are discarded because shader compilation, texture
 * upload and the GLB load all land in them and none of them recur.
 */
export interface AdaptiveQuality {
  /** Feed one frame's duration in ms. Returns the tier to switch to, or null. */
  sample(frameMs: number): QualityTier | null;
  /** Stop sampling — e.g. once the user has set a tier by hand. */
  stop(): void;
}

export function createAdaptiveQuality(
  startTier: QualityTier,
  opts: { warmupFrames?: number; windowFrames?: number; downgradeMs?: number } = {},
): AdaptiveQuality {
  const warmup = opts.warmupFrames ?? 90;
  const windowFrames = opts.windowFrames ?? 90;
  // 33.3 ms is 30 fps. Below that the game is not merely imperfect, it is
  // difficult to play, which is the only bar that justifies overriding a
  // deliberate-looking setting.
  const downgradeMs = opts.downgradeMs ?? 33.3;

  let tier = startTier;
  let seen = 0;
  let accum = 0;
  let counted = 0;
  let stopped = false;

  return {
    sample(frameMs: number): QualityTier | null {
      if (stopped || tier === 'low') return null;
      seen++;
      if (seen <= warmup) return null;
      // A single stall — an alt-tab, a GC pause, a texture upload — must not
      // trigger a downgrade, so clamp before averaging.
      accum += Math.min(frameMs, 250);
      counted++;
      if (counted < windowFrames) return null;
      const avg = accum / counted;
      accum = 0;
      counted = 0;
      if (avg <= downgradeMs) return null;
      const next = lowerTier(tier);
      if (next === tier) return null;
      tier = next;
      return next;
    },
    stop(): void {
      stopped = true;
    },
  };
}
